import React, { useEffect } from 'react';
import {
  Navigate,
  useLocation,
  RouteProps,
  RouterProps,
} from 'react-router-dom';

import { IUser, useAuth } from '../hooks/auth';
import { useToast } from '../hooks/toast';
import { isFeatureGranted } from '../utils/isFeatureGranted';
import { LockedFeaturePage } from '../components/LockedFeaturePage';

interface IRouteProps {
  isPrivate?: boolean;
  component: React.ReactElement;
  requiredPermission?: string;
  requiredFeature?: string;
}

const AuthRoute: React.FC<IRouteProps> = ({
  isPrivate = false,
  requiredPermission,
  requiredFeature,
  component,
  ...rest
}) => {
  const { pathname } = useLocation();
  const { addToast } = useToast();

  const user = localStorage.getItem('@TrenzoX:user');

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const parsedUser = JSON.parse(user) as IUser;

  // Termos/Política pendentes de aceite (cadastro anterior a essa
  // exigência, ou reset em massa após alteração do texto) -- trava
  // qualquer navegação até confirmar, exceto a própria tela de aceite.
  if (
    (!parsedUser.privacity || !parsedUser.terms_accepted) &&
    pathname !== '/accept-terms'
  ) {
    return <Navigate to="/accept-terms" replace />;
  }

  // Destino do redirect abaixo é sempre "/dashboard" -- se a checagem
  // falhar justamente NA rota "/dashboard" (ex. `user.features` ainda
  // não carregado logo após o login), navegar pra lá de novo re-executa
  // a mesma checagem e loopa ("Maximum update depth exceeded", achado em
  // produção local 2026-08-22). Só redireciona se o destino for
  // diferente de onde já estamos.
  const isAlreadyOnFallback = pathname === '/dashboard';

  if (requiredPermission) {
    if (!parsedUser.permissions?.includes(requiredPermission)) {
      if (isAlreadyOnFallback) {
        return component;
      }

      addToast({
        type: 'error',
        title: 'Acesso negado.',
        description: `Você não tem acesso a essa página.`,
      });

      return <Navigate to="/dashboard" />;
    }
  }

  // Diferente de `requiredPermission`: falta de feature não redireciona
  // -- o item de menu já mostra cadeado antes do clique (ElementMenu),
  // então a página abre normalmente só que travada, em vez de te devolver
  // pro dashboard sem explicação. Mesmo motivo elimina o risco de loop
  // que existia aqui antes (não há mais `Navigate` nesse branch).
  if (requiredFeature) {
    if (!isFeatureGranted(parsedUser.features?.[requiredFeature])) {
      return <LockedFeaturePage />;
    }
  }

  return component;
};

export { AuthRoute };
